import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import { SkeletonGrid, EmptyState } from '@/components/ui/hayat';
import axiosClient from '../../api/axiosClient';
import SeniorAvatar from '../../components/SeniorAvatar';
import { isOnline as isDeviceOnline } from '../../utils/deviceStatus';

/**
 * Phase 15.4 - Fully Integrated Senioren Page
 * With Motion, Skeletons, and Empty States
 */
function FamilySenioren() {
  const { household } = useFamilyAuth();
  const [seniors, setSeniors] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (household) {
      loadSeniors();
    }
  }, [household]);

  const loadSeniors = async () => {
    try {
      console.log('[FamilySenioren] Loading seniors for household:', household.id);
      const [seniorsRes, devicesRes] = await Promise.all([
        axiosClient.get(`/api/households/${household.id}/seniors`),
        axiosClient.get(`/api/households/${household.id}/devices`)
      ]);
      
      console.log('[FamilySenioren] ✅ Data loaded:', {
        seniors: seniorsRes.data.length,
        devices: devicesRes.data.length
      });
      
      setSeniors(seniorsRes.data);
      setDevices(devicesRes.data);
    } catch (error) {
      console.error('[FamilySenioren] ❌ Fehler beim Laden der Senioren:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeniorDevice = (seniorId) => {
    return devices.find(d => d.senior_id === seniorId);
  };

  // Skeleton Loader - Hayat Dark Theme
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] pb-24 page-transition">
        <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="animate-pulse bg-neutral-700 h-10 w-64 rounded-xl mb-2"></div>
            <div className="animate-pulse bg-neutral-700 h-6 w-32 rounded-xl"></div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SkeletonGrid count={3} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] pb-24 page-transition">
      {/* Header - Hayat Dark Theme */}
      <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="animate-fade-in-down">
            <h1 className="text-3xl font-bold text-[#F9FAFB] mb-1">
              Unsere Senioren 👨‍👩‍👧
            </h1>
            <p className="text-[#CBD5F5] font-medium">
              {seniors.length} Person{seniors.length !== 1 ? 'en' : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {seniors.length === 0 ? (
          <div className="animate-fade-in-up">
            <EmptyState
              icon="👵"
              title="Noch keine Senioren"
              description="Fügen Sie Senioren über das Caregiver-Dashboard hinzu"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {seniors.map((senior, idx) => {
              const device = getSeniorDevice(senior.id);
              // Use calculated is_online from backend as source of truth, fallback to device check
              const isOnline = senior.is_online ?? (device ? isDeviceOnline(device) : false);
              
              return (
                <div
                  key={senior.id}
                  onClick={() => navigate(`/family/seniors/${senior.id}`)}
                  className="stagger-item bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-lg hover:shadow-hayat-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group border border-neutral-600/40"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  {/* Avatar & Status - Hayat Brand Violett */}
                  <div className="relative w-24 h-24 mx-auto mb-4">
                    <SeniorAvatar 
                      senior={senior} 
                      size="lg" 
                      className="shadow-lg group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Online Status Badge */}
                    {device && (
                      <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-4 border-[#050816] shadow-lg flex items-center justify-center ${
                        isOnline 
                          ? 'bg-[#22C55E] animate-pulse' 
                          : 'bg-neutral-600'
                      }`}>
                        <span className="text-white text-xs font-bold">
                          {isOnline ? '✓' : '○'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="text-xl font-bold text-[#F9FAFB] text-center mb-4 group-hover:text-[#6DCEE8] transition-colors duration-300">
                    {senior.name}
                  </h3>

                  {/* Info Tags - Hayat Dark Theme */}
                  <div className="space-y-2">
                    {/* Language */}
                    <div className="flex items-center justify-center gap-2 px-3 py-2 bg-neutral-800/50 rounded-xl hover:bg-neutral-800 transition-colors duration-200 border border-neutral-600/40">
                      <span className="text-xl">
                        {senior.language === 'de' && '🇩🇪'}
                        {senior.language === 'tr' && '🇹🇷'}
                        {senior.language === 'ar' && '🇸🇦'}
                        {senior.language === 'en' && '🇬🇧'}
                      </span>
                      <span className="text-sm font-medium text-[#CBD5F5]">
                        {senior.language === 'de' && 'Deutsch'}
                        {senior.language === 'tr' && 'Türkisch'}
                        {senior.language === 'ar' && 'Arabisch'}
                        {senior.language === 'en' && 'Englisch'}
                      </span>
                    </div>

                    {/* Religion - Hayat Accent Gold */}
                    {senior.religion && senior.religion !== 'none' && (
                      <div className="flex items-center justify-center gap-2 px-3 py-2 bg-[#F6B352]/10 rounded-xl hover:bg-[#F6B352]/20 transition-colors duration-200 border border-[#F6B352]/40">
                        <span className="text-xl">
                          {senior.religion === 'islam' && '☪️'}
                          {senior.religion === 'christian' && '✝️'}
                        </span>
                        <span className="text-sm font-medium text-[#F6B352]">
                          {senior.religion === 'islam' && 'Islam'}
                          {senior.religion === 'christian' && 'Christlich'}
                        </span>
                      </div>
                    )}

                    {/* Online Status */}
                    {device && (
                      <div className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl transition-all duration-300 ${
                        isOnline
                          ? 'bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/40'
                          : 'bg-neutral-800/50 text-neutral-500 border border-neutral-600/40'
                      }`}>
                        <span className="text-xl">{isOnline ? '✅' : '🔴'}</span>
                        <span className="text-sm font-semibold">
                          {isOnline ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Arrow Indicator - Hayat Türkis */}
                  <div className="mt-4 text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-neutral-800/50 text-neutral-500 group-hover:bg-[#6DCEE8]/10 group-hover:text-[#6DCEE8] group-hover:scale-110 transition-all duration-300 border border-neutral-600/40 group-hover:border-[#6DCEE8]/40">
                      <span className="text-2xl">›</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default FamilySenioren;
